/**
 * Live state of a round in progress, kept in localStorage so a reload can't reset anything
 * that affects the score (e.g. places already revealed in "Whose steps?"). Written on every
 * change, read back in `init`, deleted when the round finishes.
 */
import { store } from "../game/storage";
import { MAX_HINTS, WHO_CLUES } from "../game/scoring";
import type { Level } from "../puzzles/types";

export const liveKey = (level: Level, roundId: string): string => `live:${level}:${roundId}`;

export function loadLive<T>(key: string, parse: (raw: unknown) => T | null): T | null {
  return parse(store.get<unknown>(key));
}
export const saveLive = (key: string, value: unknown): void => store.set(key, value);
export const clearLive = (key: string): void => store.del(key);

// ---------- Pin it ----------

/** Hints shown so far: a reload must not hide them again (or give the step back). */
export interface PinLive {
  hints: number;
}

export function parsePin(raw: unknown): PinLive | null {
  if (!raw || typeof raw !== "object") return null;
  const { hints } = raw as { hints?: unknown };
  return typeof hints === "number" && Number.isInteger(hints) && hints >= 0 && hints <= MAX_HINTS ? { hints } : null;
}

// ---------- Whose steps? ----------

export interface WhoTry {
  g: string;
  ok?: boolean;
  skip?: boolean;
}
/** What a reload must not undo: places revealed, wrong guesses and skips (Easy: struck names). */
export interface WhoLive {
  shown: number;
  tries: WhoTry[];
}

export const packWho = (s: WhoLive): WhoLive => ({
  shown: s.shown,
  tries: s.tries.map((x) => ({ g: x.g, ...(x.skip ? { skip: true } : {}) })),
});

/** Validate stored who state; anything malformed means "no saved state". */
export function parseWho(raw: unknown): WhoLive | null {
  if (!raw || typeof raw !== "object") return null;
  const { shown, tries } = raw as { shown?: unknown; tries?: unknown };
  if (typeof shown !== "number" || !Number.isInteger(shown) || shown < 1 || shown > WHO_CLUES) return null;
  if (!Array.isArray(tries) || tries.length > WHO_CLUES) return null;
  const out: WhoTry[] = [];
  for (const x of tries) {
    if (!x || typeof x !== "object" || typeof (x as WhoTry).g !== "string") return null;
    out.push((x as WhoTry).skip === true ? { g: "", skip: true } : { g: (x as WhoTry).g });
  }
  // Every wrong guess or skip reveals one more place, so never show fewer places than that.
  return {
    shown: Math.max(shown, Math.min(WHO_CLUES, 1 + out.length)),
    tries: out,
  };
}
